import { Duration, LocalDate, LocalDateTime } from 'js-joda';

import { core, toJSON } from '@tock/coretypes';
import { fastParseDateTime } from 'utils/timeUtils';
import AppConstants from 'constants/AppConstants';

export enum ResultState {
  SUCCESS = 1,
  WARNING = 2,
  ERROR = 3,
}

export interface CheckinVerificationStatus {
  result?: ResultState;
  message?: string;
}

interface State {
  isCheckinInProgress: boolean;
  ticketIndexForCheckinInProgress?: number;
  checkinRequest?: core.CheckinRequest;
  checkinResponse?: core.CheckinResponse;
  checkinVerificationStatus: CheckinVerificationStatus;
  selectedDate: LocalDate;
  isStatusPreviewVisible: boolean;
  isStatusDetailExpanded: boolean;
}

const initialState: State = {
  selectedDate: LocalDate.now(),
  isCheckinInProgress: false,
  checkinVerificationStatus: {},
  isStatusPreviewVisible: false,
  isStatusDetailExpanded: false,
};

function computeStatus(checkinResponse?: core.CheckinResponse): CheckinVerificationStatus {
  if (!checkinResponse) {
    return initialState.checkinVerificationStatus;
  }
  if (
    !checkinResponse.isSuccess ||
    checkinResponse.purchase === undefined ||
    checkinResponse.purchase == null
  ) {
    return {
      result: ResultState.ERROR,
      message: checkinResponse.message || 'Scan failed',
    };
  }

  // Purchase was returned from the scan
  const ticketDateTime = fastParseDateTime(checkinResponse.purchase.ticketDateTime as string);
  const ticketDate = ticketDateTime.minusHours(4).toLocalDate();
  const nowDateTime = LocalDateTime.now();
  const nowDate = LocalDate.now();

  if (!nowDate.isEqual(ticketDate)) {
    return {
      result: ResultState.WARNING,
      message: ticketDate.isAfter(nowDate)
        ? 'This ticket is valid but is for a future date'
        : 'This ticket is valid but is for a past date',
    };
  }

  const hoursDiff = Duration.between(ticketDateTime, nowDateTime).toHours();
  const message =
    hoursDiff > 3
      ? `This ticket is valid, but is for a time ${Math.abs(hoursDiff)} hours ago`
      : hoursDiff < -3
        ? `This ticket is valid, but is for a time ${Math.abs(hoursDiff)} hours from now`
        : undefined;
  return {
    result: ResultState.SUCCESS,
    message,
  };
}

export default function reducer(state: State = initialState, action: BaseTockAction): State {
  const { payload, request, type } = action;
  switch (type) {
    case AppConstants.CHECKIN_SCAN_STARTING: {
      return {
        ...state,
        isCheckinInProgress: true,
        checkinRequest: toJSON(request),
      };
    }
    case AppConstants.CHECKIN_SCAN_FAILED: {
      return {
        ...state,
        checkinRequest: undefined,
        isCheckinInProgress: false,
      };
    }
    case AppConstants.CHECKIN_SCAN_DONE: {
      return {
        ...state,
        checkinRequest: undefined,
        isCheckinInProgress: false,
        checkinResponse: payload,
        checkinVerificationStatus: computeStatus(payload),
        isStatusPreviewVisible: true,
      };
    }
    case AppConstants.CHECKIN_SCAN_RESULT_CLEAR: {
      return {
        ...state,
        checkinRequest: undefined,
        checkinResponse: undefined,
        checkinVerificationStatus: initialState.checkinVerificationStatus,
        isStatusPreviewVisible: false,
        isStatusDetailExpanded: false,
      };
    }
    case AppConstants.CHECKIN_DATE_CHANGE: {
      return {
        ...state,
        selectedDate: payload,
      };
    }
    case AppConstants.CHECKIN_TOGGLE_EXPAND_STATUS_DETAIL: {
      return {
        ...state,
        isStatusDetailExpanded: payload,
      };
    }
    default: {
      return state;
    }
  }
}
